import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, of, Subject } from 'rxjs';
import {catchError, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import { ConfirmationService, MessageService } from 'primeng/api';

// Services / Subjects
import { CatalogService } from '../../../services/catalog.service';
import { FileService } from '../../../services/file.service';
import { PapersService } from '../../../services/papers.service';
import { PropertyService } from '../../../services/property.service';
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";

// DTOs / Enums
import { FileInformation } from '../../../dto/file.information.dto';
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

interface DocumentFile {
    id: number;
    name: string;
    url: string;
    size?: number;
}

interface DocumentItem {
    paperId: number;
    catalogLabel: string;
    label: string;
    statusOptions: any[];
    selectedStatus: any;        // opción del catálogo
    files: DocumentFile[];
    uploading?: boolean;
}

const STEP_KEY = 'papers';

@Component({
    selector: 'app-property-papers',
    templateUrl: './property-papers.component.html',
    styleUrls: ['./property-papers.component.scss'],
    providers: [ConfirmationService, MessageService],
})
export class PropertyPapersComponent implements OnInit, OnDestroy {

    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId?: number;

    // --------- UI / estado -------------
    segmentName = '';
    isPowerSelected = false;
    loading = true;
    private destroy$ = new Subject<void>();

    // --------- Documentos -------------
    documents: DocumentItem[] = [];

    // Documento especial para "Poder Notariado"
    powerDocument: DocumentItem = {
        paperId: -999,                 // id simbólico; si tu backend lo soporta, mapea CT_POWER
        catalogLabel: 'CT_POWER',
        label: 'Poder notariado',
        statusOptions: [],
        selectedStatus: { optionName: 'Cargado' }, // por defecto
        files: [],
    };

    constructor(
        private router: Router,
        private catalogService: CatalogService,
        private fileService: FileService,
        private papersService: PapersService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService,
        private confirm: ConfirmationService,
        private messageService: MessageService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Estado global del paso
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) {
            this.router.navigate(['/list-pending']); return;
        }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentName = this.propertyInitial.segmentLabel;
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Carga de documentos + catálogos
        this.getPaperList(this.propertyId);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga inicial (documentos + opciones por doc)
    // -----------------------------------------------------------
    private getPaperList(propertyId: number): void {
        this.loading = true;

        this.papersService.getPaperList(propertyId).pipe(
            tap((papers: any[]) => {
                const all = papers || [];
                // El "Poder notariado" se maneja aparte (checkbox + upload). Sólo aparece en la lista
                // cuando está activo (status=1); capturamos su paperId REAL para no subir con -999.
                const powerPaper = all.find((p: any) => p.ctPaperType === 'POWER_OF_ATTORNEY');
                if (powerPaper) {
                    this.powerDocument.paperId = powerPaper.paperId;
                    this.powerDocument.label = powerPaper.optPaperType || this.powerDocument.label;
                    this.powerDocument.files = (powerPaper.fileList || []).map((file: any) => ({
                        id: file.fileId,
                        name: file.fileName || file.fileCategory,
                        url: file.fileUrl,
                        size: file.size,
                    }));
                    this.isPowerSelected = true;
                } else {
                    this.powerDocument.paperId = -999;
                    this.powerDocument.files = [];
                    this.isPowerSelected = false;
                }
                this.documents = all
                    .filter((paper: any) => paper.ctPaperType !== 'POWER_OF_ATTORNEY')
                    .map((paper: any) => ({
                        paperId: paper.paperId,
                        catalogLabel: paper.ctPaperType,
                        label: paper.optPaperType,
                        statusOptions: [],
                        selectedStatus: paper.ctPaperStatus, // backend puede devolver objeto u opción
                        files: (paper.fileList || []).map((file: any) => ({
                            id: file.fileId,
                            name: file.fileName || file.fileCategory,
                            url: file.fileUrl,
                            size: file.size,
                        })),
                    }));
            }),
            switchMap(() => {
                // Cargar opciones de status por cada documento
                const reqs = this.documents.map((doc) =>
                    this.catalogService.getCatalogOptionList('CT_PAPER_STATUS', doc.catalogLabel)
                );
                return forkJoin(reqs).pipe(
                    tap((results: any[]) => {
                        this.documents.forEach((doc, i) => (doc.statusOptions = results[i]));
                    })
                );
            }),
            finalize(() => (this.loading = false)),
            catchError(() => {
                this.loading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudieron cargar los documentos.',
                });
                return of([]);
            })
        ).subscribe({
            next: () => {
                // aplicar cache local si existe (estatus, poder, etc.)
                this.applyCache();
            }
        });
    }

    // -----------------------------------------------------------
    // Cache local (FlowState)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            isPowerSelected: this.isPowerSelected,
            docs: this.documents.map(d => ({
                paperId: d.paperId,
                selectedStatus: d.selectedStatus,
                // No guardamos archivos en cache: ya viven en backend
            }))
        });
    }

    private applyCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        if (!cached) return;

        // isPowerSelected ya lo fija getPaperList desde el backend (presencia del paper activo) — no lo pisamos.
        // Reconciliar estatus guardados localmente
        if (Array.isArray(cached.docs)) {
            const byId = new Map<number, any>(cached.docs.map((x: any) => [x.paperId, x.selectedStatus]));
            this.documents.forEach(d => {
                const cachedStatus = byId.get(d.paperId);
                if (cachedStatus) {
                    d.selectedStatus = cachedStatus;
                }
            });
        }
    }

    // -----------------------------------------------------------
    // Handlers UI
    // -----------------------------------------------------------
    onSelectValidate(event: any): void {
        const files = this.extractFiles(event);
        const MAX = 10 * 1024 * 1024;

        if (!files.length) return;

        const invalids: string[] = [];
        for (const f of files) {
            const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
            if (!isPdf) invalids.push(`${f.name}: formato inválido`);
            if (f.size > MAX) invalids.push(`${f.name}: excede 10 MB`);
        }

        if (invalids.length) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Validación',
                detail: invalids.join(' | '),
                life: 6000,
            });
        }
    }


    /** Subida de archivos (customUpload + auto=true) */
    onUpload(event: any, document: DocumentItem, uploader: any): void {
        if (!this.propertyId) return;

        const files = this.extractFiles(event);
        if (!files.length) return;

        document.uploading = true;

        const upload$ = forkJoin(
            files.map((file) =>
                this.fileService.uploadPaperFile(this.propertyId!, document.paperId, file)
            )
        );

        upload$
            .pipe(
                switchMap((uploaded: FileInformation[]) => {
                    const renameCalls = uploaded.map((res, i) =>
                        this.fileService.putFileName(res.fileId, { data: files[i].name }).pipe(
                            catchError(() => {
                                this.messageService.add({
                                    severity: 'warn',
                                    summary: 'Nombre no guardado',
                                    detail: `No se pudo guardar el nombre para ${files[i].name}`,
                                });
                                return of(null);
                            }),
                            map(() =>
                                ({
                                    id: res.fileId,
                                    name: files[i].name ?? res.fileName,
                                    url: res.fileUrl,
                                    size: (res as any).size ?? files[i].size ?? 0,
                                } as const)
                            )
                        )
                    );

                    return renameCalls.length ? forkJoin(renameCalls) : of([]);
                }),
                finalize(() => (document.uploading = false)),
                catchError(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudieron subir los archivos.',
                    });
                    return of([]);
                })
            )
            .subscribe((finalFiles: Array<{ id: number; name: string; url: string; size: number } | null>) => {
                finalFiles.filter(Boolean).forEach((f: any) => document.files.push(f));
                uploader?.clear?.();
                this.persistToFlowState?.();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Listo',
                    detail: 'Archivo(s) subido(s) correctamente.',
                });
            });
    }


    onBeforeUpload(document: DocumentItem): void {
        if (!document.selectedStatus) {
            this.messageService.add({
                severity: 'info',
                summary: 'Selecciona el estatus',
                detail: `Antes de subir, indica el estatus para "${document.label}".`,
            });
        }
    }

    confirmDelete(file: DocumentFile, document: DocumentItem): void {
        this.confirm.confirm({
            message: `¿Eliminar "${file.name}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.deleteFile(file, document),
        });
    }

    private deleteFile(file: DocumentFile, document: DocumentItem): void {
        if (!file?.id) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'El archivo no tiene un ID válido.',
            });
            return;
        }

        this.fileService.deleteFile(file.id).pipe(
            catchError(() => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo eliminar el archivo.',
                });
                return of(null);
            })
        ).subscribe(() => {
            document.files = document.files.filter((f) => f.id !== file.id);
            this.persistToFlowState();

            this.messageService.add({
                severity: 'success',
                summary: 'Eliminado',
                detail: 'Archivo eliminado correctamente.',
            });
        });
    }

    onStatusChanged(document: DocumentItem): void {
        // Persistir estatus de un documento
        const data = {
            ctPaperStatus: document.selectedStatus?.optionCode || null,
            paperDetail: document.label,
        };

        this.papersService.updateParperStatus(document.paperId, data).pipe(
            catchError(() => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: `No se pudo actualizar el estatus de "${document.label}".`,
                });
                return of(null);
            })
        ).subscribe(() => {
            this.persistToFlowState();
        });
    }

    onPowerSelectionChange(): void {
        if (!this.propertyId) {
            // Aún no hay propiedad creada: el checkbox togglea solo localmente.
            this.persistToFlowState();
            return;
        }
        const desired = this.isPowerSelected; // valor YA togglado por ngModel (marcar o desmarcar)
        this.propertyService.updatePropertyOwnerStatus(this.propertyId).subscribe({
            next: () => {
                this.persistToFlowState();
                // Re-cargamos la lista: el backend ya activó/desactivó el paper del poder, así
                // capturamos (o limpiamos) su paperId REAL y dejamos isPowerSelected en sync.
                if (this.propertyId) this.getPaperList(this.propertyId);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Poder actualizado',
                    detail: desired
                        ? 'El poder notariado se ha seleccionado correctamente.'
                        : 'Se quitó el poder notariado.',
                });
            },
            error: () => {
                this.isPowerSelected = !desired; // revertir ante error
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'No se pudo actualizar el poder notariado.',
                });
            },
        });
    }

    // -----------------------------------------------------------
    // Navegación + validación + avanzar etapa (según política)
    // -----------------------------------------------------------
    handleNextStep(): void {
        // Validación: si NO hay poder, cada documento requiere estatus + al menos 1 archivo
        if (!this.isPowerSelected) {
            for (const doc of this.documents) {
                const hasStatus = !!doc.selectedStatus;
                const hasFile = (doc.files?.length ?? 0) > 0;
                if (!hasStatus || !hasFile) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Validación',
                        detail: `Selecciona el estatus y sube al menos un archivo para "${doc.label}".`,
                    });
                    return;
                }
            }
        }

        // cache final
        this.persistToFlowState();

        if (this.shouldAdvanceStage()) {
            const body = { id: this.propertyId };
            this.propertyService.nextStageProperty(body).pipe(
                catchError((err) => { console.error('nextStage error', err); return of(null); })
            ).subscribe(() => this.router.navigate([this.nextUrl]));
            return;
        }

        // No avanzar etapa (EDIT o RESUME en mismo/anterior paso)
        this.router.navigate([this.nextUrl]);
    }

    handleSaveAndExit(): void {
        this.persistToFlowState();
        // no avanzamos etapa
        this.router.navigate(['/list-pending']); // o dashboard
    }

    handlePreviousStep(): void {
        this.router.navigate([this.previousUrl]);
    }

    // Política para avanzar etapa:
    // - CREATE → avanza
    // - EDIT → no avanza
    // - RESUME → avanza sólo si estás en un paso **posterior** al recordStage
    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        // RESUME
        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx; // estamos después → avanzamos
    }

    // -----------------------------------------------------------
    // trackBy helpers
    // -----------------------------------------------------------
    trackByPaper = (_: number, d: DocumentItem) => d.paperId;
    trackByFile = (_: number, f: DocumentFile) => f.id;

    // helper robusto
    private extractFiles(event: any): File[] {
        const candidate =
            event?.files ??
            event?.currentFiles ??
            event?.target?.files ??
            event; // fallback extremo

        if (Array.isArray(candidate)) return candidate as File[];
        if (candidate instanceof FileList) return Array.from(candidate as FileList);
        if (candidate instanceof File) return [candidate as File];
        return [];
    }

}
